import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { AppointmentTemplateEnvelope } from '@/types/settings-api';

/**
 * Canonical query-key factory for the appointment-template list. Same
 * pattern as the other query-key factories so mutations + tests can
 * target the slot without inlining the array literal.
 */
export function appointmentTemplateListQueryKey(
  doctorSetupId: string | null | undefined,
  organizationId: string | null | undefined,
): readonly [string, string, string | null | undefined, string | null | undefined] {
  return [
    'settings',
    'appointment-template-list',
    doctorSetupId,
    organizationId,
  ];
}

/**
 * Practitioner appointment-template list — PROD-762 PR-G.
 *
 * GET /api/Doctor/AppointmentTemplateList?practionerId={doctorId}&organizationId={orgId}
 *
 * **Wire-typo on the query string**: backend expects `practionerId`,
 * not `practitionerId`. Same typo as AppointmentProfileList.
 *
 * **Field-name asymmetry on this endpoint family** (verified 2026-05-15):
 * the body returned uses CORRECT `practitionerId` on each template
 * entry. So this endpoint has the typo on the URL parameter but the
 * correct spelling on the response body. Don't try to harmonise —
 * see `AppointmentTemplateEntry` doc for the full quirk story.
 *
 * Returns:
 *   {
 *     request: AppointmentTemplateEntry[],
 *     isDeleted: string[]   // empty [] on GET
 *   }
 *
 * Each entry carries `templateName`, `weekDay[]`, `startTime`/`endTime`
 * (split int+marker), `appointmentProfileId` (references an entry from
 * `useAppointmentProfileList`), `isActive` flag.
 *
 * Flutter source: `settings_data_source_impl.dart:353-370`
 * (`getAppointmentTemplates`).
 */
export function useAppointmentTemplateList(
  doctorSetupId: string | null | undefined,
  organizationId: string | null | undefined,
) {
  return useQuery({
    queryKey: appointmentTemplateListQueryKey(doctorSetupId, organizationId),
    queryFn: ({ signal }) => {
      if (!doctorSetupId || !organizationId) {
        throw new Error(
          'useAppointmentTemplateList called without doctorSetupId or organizationId',
        );
      }
      const qs = new URLSearchParams({
        practionerId: doctorSetupId, // verbatim typo per wire contract
        organizationId,
      });
      return get<AppointmentTemplateEnvelope>(
        `/api/Doctor/AppointmentTemplateList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!doctorSetupId && !!organizationId,
  });
}
